import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
  useContext,
} from "react";
import Numeral from "../../../components/numeral/Numeral";
import { Tertiary } from "../../../components/buttons/Tertiary";
import { HttpRequest } from "../../../services/httpRequest";
import { CreditUserContext } from "../../../context/CreditUser/CreditUserContext";
import { determinarMora2 } from "../../../utils/determinarMora";
import { SkeletonDahsboard } from "../../../components/skeleton/skeletonDash";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { SuccessAlert } from "../../../components/alerts/successAlert";
import { ErrorAlert } from "../../../components/alerts/errorAlert";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { SecondaryCard } from "../../../components/buttons/SecondaryCard";
import "../../../components/slider/swiper.css";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import { returnToSignin } from "../../../utils/returnToSign";
import {
  MenuEllipsis,
  MenuEllipsisMobile,
} from "../../../components/buttons/navigation/MenuEllipsis";
import { CreditsService } from "../../../services/credits/credits.service";
import { formatAmount, formatFecha, optionsMenuEllipsis } from "./utils";
import { NotificationsService } from "../../../services/notifications/notifications.service";
import { saveNotificationsToLocalStorage } from "../../../components/notifications/helper";
import { notificationConfigs } from "../../../utils/notificationConfigs";
import { useCredit } from "../../../context/creditContext";
import {
  registerServiceWorker,
  requestPermissionNotification,
  subscribeUser,
} from "../../../utils/webpush/webpushUtils";
import { APIClient } from "../../../helpers/api_helper";
import * as url from "../../../helpers/url_helper";
import { RainbowRangeBasedUpperSemiCircularProgress } from "../../../components/userScore/SemiCircularProgressScore";
import { disbursementValue } from "../../../utils/disbursement/disbursement.utils";
import { IndexedDBService } from "../../../services/indexedDbService";
import moment from "moment";
import { creditStatus } from "../../flujo/Utils";
import { stepsCreditStore } from "../../../store/creditStore/stepsCreditStore";
import { userBankStore } from "../../../store/creditStore/userBankStore";
import { useCreditStore } from "../../../store/creditStore/creditUserStore";
import { creditOfferStore } from "../../../store/creditStore/offerCreditStore";

import { LoginSuccessAnimation } from "../../../components/new_components/login-success-animation";
import { ANIMATION_DURATIONS } from "../../../configs/animations";
import axios from "axios";
import { resolverDeudaHoy } from "../../../utils/deuda.utils";
import { clearUtmTracking } from "../../../services/utmleads/utmReferral";

function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

const getInstanceAxios = () => {
  const axiosInstance = axios.create({
    withCredentials: false, // Incluye las credenciales (cookies) en la solicitud
  });
  return axiosInstance;
};

const apiClient = new APIClient();
const indexedDbService = new IndexedDBService();

const VIEW_PHASE = {
  ANIMATION: "ANIMATION",
  SKELETON: "SKELETON",
  DATA: "DATA",
};

export function MyCreditPage(props) {
  const [viewPhase, setViewPhase] = useState(() => {
    const isLoadingLogin = localStorage.getItem("is_loading_login");
    return isLoadingLogin === "true"
      ? VIEW_PHASE.ANIMATION
      : VIEW_PHASE.SKELETON;
  });
  const [dataReady, setDataReady] = useState(false);


  const [, setScreenSize] = React.useState(getCurrentDimension());
  const { updateDatosVarios, datosVarios } = useContext(CreditUserContext);
  const { setCreditId, disclosures, creditInfo, due, setQuotaInfo } =
    useCredit();

  const navigate = useNavigate();

  const location = useLocation();

  let provieneFirma = false;
  let doc = "";
  if (location?.state) {
    provieneFirma = location?.state?.provieneFirma
      ? location?.state?.provieneFirma
      : false;
    doc = location?.state?.doc ? location?.state?.doc : "";
  }

  // Refs con los valores mas recientes de doc/provieneFirma (derivados de
  // location.state) para que las funciones de carga -memoizadas con
  // useCallback([])- no necesiten estos valores como dependencia.
  const docRef = useRef(doc);
  const provieneFirmaRef = useRef(provieneFirma);
  useEffect(() => {
    docRef.current = doc;
    provieneFirmaRef.current = provieneFirma;
  });

  // Instancia de axios estable entre renders (evita crear un cliente nuevo
  // en cada render y tener que declararlo como dependencia de los efectos).
  const axiosInstanceRef = useRef(null);
  if (axiosInstanceRef.current === null) {
    axiosInstanceRef.current = getInstanceAxios();
  }

  const [basicInfo, setBasicInfo] = useState({});
  const [creditsInfo, setCreditsInfo] = useState([]);
  const [records, setRecords] = useState([]);
  const [features, setFeatures] = useState([]);
  const [messsageAlert, setMessageAlert] = useState("");
  const [isOpenSuccess, setIsOpenSuccess] = useState();
  const [isOpenError, setIsOpenError] = useState();
  const [viewalert, setViewalert] = useState(false);
  const [hasMora, setHasMora] = useState(false);
  const [daysMora, setDaysMora] = useState(0);
  const [scoreEngine, setScoreEngine] = useState(1);
  const [colorRanges, setColorRanges] = useState([]);
  const [disbursementData, setDisbursementData] = useState({});
  const [optionsMenuEllipsisCard, setOptionsMenuEllipsisCard] =
    useState(optionsMenuEllipsis);

  const [, setIsMobile] = useState(false);

  const handleOpenAlert = useCallback((type, message, isOpen) => {
    if (type === "success") {
      setMessageAlert(message);
      setIsOpenSuccess(isOpen);
      setViewalert(true);
    }
    if (type === "error") {
      setMessageAlert(message);
      setIsOpenError(isOpen);
      setViewalert(true);
    }
  }, []);

  const cleanIndexDB = useCallback(() => {
    // Ver providers/session/inactivityProvider.jsx: encadenar 4 stores de
    // zustand reactivos solo para llamar sus acciones de limpieza dispara 4
    // "useSyncExternalStore" seguidos que, bajo cierta temporizacion,
    // pueden romper con "Should have a queue" (Rules of Hooks). Estas 4
    // acciones no necesitan suscripcion reactiva, se leen via ".getState()".
    useCreditStore.getState().clearUser();
    userBankStore.getState().clearUserBank();
    creditOfferStore.getState().clearOfertCredit();
    stepsCreditStore.getState().clearSteps();
  }, []);

  useEffect(() => {
    if (localStorage.getItem("fromConfirmed") !== "true") return undefined;

    const hasUtmTrackingData = [
      "utmReferral",
      "click_id",
      "utm_id",
      "partnerId",
      "visitor",
      "operationIdUtm",
      "hasNotifiedUtm",
    ].some((key) => localStorage.getItem(key) !== null);

    if (!hasUtmTrackingData) return undefined;

    clearUtmTracking();
  }, []);

  const handleClearStateAlert = useCallback(() => {
    setViewalert(false);
    setMessageAlert("");
    setIsOpenSuccess(false);
    setIsOpenError(false);
  }, []);

  const loadCredits = useCallback(async () => {
    try {
      const data = await HttpRequest.getAllCreditUser(
        provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getLastActivity = useCallback(async () => {
    try {
      const data = await HttpRequest.getLastActivity(
        provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
        4,
      );
      return data.message.activity;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoUser = useCallback(async () => {
    try {
      const data = await HttpRequest.getInfoUser(
        provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoAuth = useCallback(async () => {
    try {
      const data = await HttpRequest.getUserAuth(
        provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
      );
      return data?.message.user;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const loadInfoQuota = useCallback(async () => {
    try {
      const data = await HttpRequest.getQuotaStatus(
        provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getScoreEngineSettings = useCallback(async () => {
    try {
      let { data } = await apiClient.get(url.GET_SETTING_BY_NAME, {
        name: "score-engine-config",
      });
      let value = data?.data?.value;
      return value;
    } catch (error) {
      console.error(error);
      return [];
    }
  }, []);

  const handleSetRangeColor = useCallback(async () => {
    let value = await getScoreEngineSettings();
    let ranges = value[0]?.ranges;
    setColorRanges(ranges);
    return;
  }, [getScoreEngineSettings]);

  const getLastCredits = useCallback(async (limit = 5, page = 1) => {
    try {
      const { message } = await CreditsService.getCreditsPaginate({
        filter: {
          documento: provieneFirmaRef.current
            ? docRef.current
            : localStorage.getItem("doc"),
          status: ["Vigente", "Cancelado"],
        },
        limit,
        page,
      });
      return message.info;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getInterest = useCallback(async () => {
    try {
      let currentInterestRate = localStorage.getItem("currentInterestRate");
      let currentInterestTea = localStorage.getItem("currentInterestTea");
      if (
        currentInterestRate &&
        currentInterestRate !== "null" &&
        currentInterestTea &&
        currentInterestTea !== "null"
      ) {
        return;
      }
      let response = await apiClient.get(url.GET_RATES_CURRENT_INTEREST);
      const record = response.data.data;
      let interest = record.ted ?? record.tasa ?? null;
      let tea = record.tea ?? null;
      localStorage.setItem("currentInterestRate", interest);
      localStorage.setItem("currentInterestTea", tea);
      return;
    } catch (error) {
      console.log(error);
      return;
    }
  }, []);

  const getInfoDisbursement = useCallback(async (credit_id) => {
    try {
      if (!credit_id) return {};
      const { data } = await apiClient.get(
        url.GET_INFO_DISBURSEMENT + credit_id,
      );
      let _disbursementData = data?.data;
      return _disbursementData ?? {};
    } catch (error) {
      console.error(error);
      return {};
    }
  }, []);

  const parseLastCredits = useCallback((lastCredits) => {
    let records = (lastCredits || []).map((credit, index, array) => {
      return {
        id: `N.${(credit?.credit_id || "").substring(0, 7)}`,
        ammount: formatAmount(credit?.approvedQuota || "200000"),
        date:
          moment(credit?.signatureDate, "DD/MM/YYYY HH:mm:ss").format(
            "DD/MM/YYYY",
          ) || credit?.acceptanceDate,
        state: credit?.status,
      };
    });
    setRecords(records);
  }, []);

  const handleUpdateDataIndexDb = useCallback(async (myRewards, rewardsPending) => {
    try {
      let idRewards = "rewards";
      let item = await indexedDbService.findItem("id", idRewards);
      if (item) {
        indexedDbService.updateItem(item.id, { myRewards, rewardsPending });
      } else {
        await indexedDbService.addItem({
          myRewards,
          id: idRewards,
          rewardsPending,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleUpdateQuotaInfo = useCallback(async (quotaInfo) => {
    try {
      let idQuota = "quota";
      let item = await indexedDbService.findItem("id", idQuota);
      if (item) {
        indexedDbService.updateItem(item.id, { quotaInfo });
      } else {
        await indexedDbService.addItem({ quotaInfo, id: idQuota });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleGetMyRewards = useCallback(async () => {
    try {
      let rewardsPending = 0;
      let myRewards = [];
      let { data, status } = await apiClient.get(
        `${url.GET_MY_REWARDS}${localStorage.getItem("doc")}`,
      );
      if (status === 200 && "data" in data) {
        let rewardsData = data?.data ?? [];
        if (Array.isArray(rewardsData) && rewardsData.length > 0) {
          let _rewardsPendings = rewardsData.filter((re) => re?.used === false);
          rewardsPending = _rewardsPendings.length;
          myRewards = rewardsData;
        }
      }
      handleUpdateDataIndexDb(myRewards, rewardsPending).catch((e) =>
        console.log(e),
      );
    } catch (error) {
      console.log(error);
    }
  }, [handleUpdateDataIndexDb]);

  const handleAnimationComplete = useCallback(() => {
    localStorage.setItem("is_loading_login", "false");
  }, []);

  const validateDisbursementValue = useCallback(async (creditInfo, _disbursementData) => {
    localStorage.setItem("disbursementStatus", _disbursementData?.status);
    localStorage.setItem("disbursementComment", _disbursementData?.comment);
    localStorage.setItem("disbursementId", _disbursementData?.disbursementId);
    if (creditInfo?.hasNovation === true) return "Novación";

    if (
      Object.keys(_disbursementData).length === 0 ||
      _disbursementData.status === "En proceso"
    ) {
      if (
        creditInfo.signatureDate !== null &&
        creditInfo.signatureDate !== ""
      ) {
        try {
          const dayAfter = moment(creditInfo?.signatureDate, "DD/MM/YYYY").add(
            1,
            "days",
          );

          const holyDaysResponse = await axiosInstanceRef.current.get(
            url.GET_HOLIDAYS + dayAfter.format("YYYY"),
          );

          const holidays = holyDaysResponse.data.holidays.map((h) =>
            moment(h.date, "YYYY-MM-DD").format("YYYY-MM-DD"),
          );

          let nextWorkingDay = dayAfter.clone();

          const dayName = dayAfter.format("dddd");

          if (dayName === "sábado") {
            nextWorkingDay.add(2, "days");
          } else if (dayName === "domingo") {
            nextWorkingDay.add(1, "days");
          }

          // 🟡 Validación SOLO de festivos
          while (holidays.includes(nextWorkingDay.format("YYYY-MM-DD"))) {
            nextWorkingDay.add(1, "days");
          }

          if (_disbursementData?.commentToUser) {
            return _disbursementData?.commentToUser;
          } else {

            const finalDayName = nextWorkingDay.format("dddd");
            const finalDayNumber = nextWorkingDay.format("DD");
            const finalMonthName = nextWorkingDay.format("MMMM");
            return `${disbursementValue[_disbursementData.status]} ${finalDayName} ${finalDayNumber} de ${finalMonthName}`;
          }
        } catch (error) {
          console.error(error);
        }
      }
      return _disbursementData?.commentToUser
        ? _disbursementData?.commentToUser
        : disbursementValue[_disbursementData.status];
    }

    if (_disbursementData?.status === "Exitoso") {
      if (_disbursementData?.commentToUser) {
        return _disbursementData?.commentToUser;
      } else {
        const dateDisbursement = moment(
          _disbursementData.disbursementDate,
        ).utc();

        const finalDayName = dateDisbursement.format("dddd");
        const finalDayNumber = dateDisbursement.format("DD");
        const finalMonthName = dateDisbursement.format("MMMM");
        return `${disbursementValue[_disbursementData.status]} ${finalDayName} ${finalDayNumber} de ${finalMonthName}`;
      }
    }
    return _disbursementData?.commentToUser
      ? _disbursementData?.commentToUser
      : disbursementValue[_disbursementData.status];
  }, []);

  const configData = useCallback((
    creditInfo,
    basicInfo,
    quotaInfo,
    deudaHoy,
    _disbursementData,
    disbursementState,
  ) => {
    let status = creditInfo?.status;
    let title1 = "";
    let title2 = "";
    if (
      status === "Solicitud rechazada" ||
      status === "Solicitud Incompleta" ||
      status === "Solicitado" ||
      status === "Pendiente de firmar" ||
      status === "En mora" ||
      status === "Desistido" ||
      status === "Cancelado"
    ) {
      title1 = "0";
      title2 = "No tienes créditos vigentes";
    } else {
      localStorage.setItem("credit_id", creditInfo?.credit_id);
      title1 = `N. ${creditInfo?.credit_id.substring(0, 8) || ""}`;
      title2 = creditInfo?.paydayLimit || "";
    }

    let features = [
      {
        title: "Créditos vigentes",
        title2: title1,
        classBox:
          "w-full rounded-2xl shadow-2xl transition p-4 bg-purple text-white",
        details: [
          {
            nameO: "Deuda a la fecha",
            numberO: deudaHoy ?? "0",
            id: 0,
            disbursementName: "Estado de desembolso",
            disbursementValue: disbursementState,
          },
          {
            nameD: "Fecha límite de pago",
            value: title2,
            id: 1,
          },
        ],
      },
    ];

    setFeatures(features);
    return;
  }, []);

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };
    window.addEventListener("resize", updateDimension);

    window.removeEventListener("resize", updateDimension);

    const validSessionAndTokenJwt = async () => {
      try {
        let isValidToken = await validateSessionAndJwtToken();
        return isValidToken;
      } catch (error) {
        return false;
      }
    };
    const userExistBasicInfo = async () => {
      try {
        setViewalert(false);
        setMessageAlert("");
        setIsOpenSuccess(false);
        setIsOpenError(false);
        let response = await HttpRequest.validateInfoBasicUser(
          provieneFirmaRef.current ? docRef.current : localStorage.getItem("doc"),
        );
        if (response.error) {
          return true;
        }
        return response.message.message;
      } catch (error) {
        console.log(error);
        return true;
      }
    };

    const loadCreditsUser = async () => {
      let credits = await loadCredits();
      return credits;
    };

    const loadInfo = async () => {
      let info = await loadInfoUser();
      return info;
    };

    const loadAuthInfo = async () => {
      let authInfo = await loadInfoAuth();
      return authInfo;
    };

    const loadQuotaStatus = async () => {
      let quotaStatus = await loadInfoQuota();
      return quotaStatus;
    };

    const loadLastActivity = async () => {
      let lastActivity = await getLastActivity();
      return lastActivity;
    };

    const loadLastCreditsPaginate = async (limit, page) => {
      let lastCredits = await getLastCredits(limit, page);
      return lastCredits;
    };

    const loadNotificationsByUser = async (userId, page, limit) => {
      let lastNotifications = await NotificationsService.getNotifications(
        userId,
        page,
        limit,
      );
      return lastNotifications;
    };

    const determinaCreditoAMostrar = (credits = []) => {
      const estadosPrioritarios = [
        "Vigente",
        "Pendiente de firmar",
        "En mora",
        "Cancelado",
        "Solicitado",
        "Solicitud rechazada",
        "Solicitud Incompleta",
        "Desistido",
        "Cupo disponible",
        "",
      ];

      for (const estado of estadosPrioritarios) {
        const filtrados = credits.filter((credit) => credit.status === estado);
        if (filtrados.length > 0) {
          return filtrados;
        }
      }

      return []; // En caso de que no haya créditos en ningún estado
    };

    const handleInitView = async () => {
      let credit;
      let info;
      let deudahoy;
      let authInfo;
      let lastActivity;
      try {
        handleGetMyRewards();
        getInterest();
        handleSetRangeColor();
        loadNotificationsByUser(
          localStorage.getItem("doc"),
          notificationConfigs.initNotificationsPage,
          notificationConfigs.initNotificationsRows,
        )
          .then((response) => {
            let notifications = response?.notifications ?? [];
            saveNotificationsToLocalStorage(notifications);
          })
          .catch((e) => console.log(e));

        let quotaStatus = await loadQuotaStatus();
        let hasInfoBasic = await userExistBasicInfo();
        let lastCredits = await loadLastCreditsPaginate(5, 1);
        handleUpdateQuotaInfo({
          cupoDisponible: quotaStatus?.cupoDisponible,
          cupoAprobado: quotaStatus?.cupoAprobado,
          cupoUtilizado: quotaStatus?.cupoUtilizado,
        }).catch((e) => console.log(e));
        parseLastCredits(lastCredits);
        if (!hasInfoBasic) {
          cleanIndexDB();
          navigate("/request-credit");
          return;
        }
        info = await loadInfo();
        authInfo = await loadAuthInfo();
        lastActivity = await loadLastActivity();
        localStorage.setItem("phone", authInfo?.phone);

        localStorage.setItem("email", authInfo?.email);
        localStorage.setItem("level", authInfo?.levelInfo?.name ?? 1);
        localStorage.setItem("percentLevel", authInfo?.percentLevel ?? 0);
        localStorage.setItem("lastActivity", JSON.stringify(lastActivity));
        localStorage.setItem(
          "firstname",
          info?.firstname ? capitalizeFirstLetter(info?.firstname) : "",
        );
        localStorage.setItem("score", authInfo?.score);
        localStorage.setItem("scoreForNextLevel", authInfo?.scoreForNextLevel);
        localStorage.setItem(
          "avatar",
          info?.gender === "Masculino" || info?.gender === "1"
            ? authInfo?.levelInfo?.avatar?.man
            : authInfo?.levelInfo?.avatar?.woman,
        );
        setBasicInfo(info);
        setScoreEngine(authInfo?.scoreEngine ?? 1);
        let creditData = await loadCreditsUser();
        /* console.log(creditData); */
        if (creditData.length === 0) {
          alert("No tienes creditos activos.");
          cleanIndexDB();
          navigate("/");
          handleClearStateAlert();
          localStorage.clear();
          return;
        }
        credit = determinaCreditoAMostrar(creditData);
        if (credit.length === 0 || credit[0].status === "") {
          alert("No tienes creditos activos.");
          cleanIndexDB();
          navigate("/");
          handleClearStateAlert();
          localStorage.clear();
          return;
        }
        if (credit[0].status === "Cancelado") {
          updateDatosVarios({
            deudaHoy: "0",
            hasMora: "0",
            diasMora: "0",
            statusCredit: credit[0].status,
            cupoDisponible: quotaStatus,
          });
          localStorage.setItem("credit_id", credit[0]?.credit_id);
          setOptionsMenuEllipsisCard(
            optionsMenuEllipsis.filter((item) => item.name !== "creditDetails"),
          );
        } else if (credit[0].status === "Vigente") {
          let moraData = determinarMora2(credit[0]?.paydayLimit);
          try {
            const result = await resolverDeudaHoy(credit[0], quotaStatus);

            if (result) {
              deudahoy = result.deudaHoy;
            }
          } catch (error) {
            console.log(error);
          }
          if (moraData.hasMora) {
            setHasMora(true);
            setDaysMora(isNaN(moraData.diasMora) ? 0 : moraData.diasMora);
            await HttpRequest.registrarDiasEnMora({
              diasMora: moraData.diasMora,
              documento: credit[0].documento,
              credit_id: credit[0].credit_id,
            });
          }
          updateDatosVarios({
            deudaHoy: deudahoy,
            hasMora: moraData.hasMora,
            diasMora: moraData.diasMora,
            statusCredit: credit[0].status,
            cupoDisponible: quotaStatus?.cupoDisponible,
            dctoFirmaElectronica: credit[0].dctoFirmaElectronica,
          });
        } else if (credit[0].status === creditStatus.PF) {
          localStorage.setItem("credit_id", credit[0]?.credit_id);
          if (creditData.length > 1) {
            const hasCreditsInCancelado = creditData.some(
              (credit) => credit.status === "Cancelado",
            );

            if (hasCreditsInCancelado) {
              // return navigate(`/oferta`);
            } else {
              navigate(
                `/request-credit?statusCredit=${credit[0].status}&credit_id=${credit[0].credit_id}`,
              );
              return;
            }
          } else {
            navigate(
              `/request-credit?statusCredit=${credit[0].status}&credit_id=${credit[0].credit_id}`,
            );
            return;
          }
        } else if (credit[0].status === creditStatus.S) {
          cleanIndexDB();
          navigate("/request-credit");
        } else if (credit[0].status === "Solicitud Incompleta") {
          cleanIndexDB();
          navigate("/request-credit");
          return;
        } else if (credit[0].status === "Solicitud rechazada") {
          handleOpenAlert(
            "error",
            "Lo sentimos, por el momento tu solicitud ha sido rechazada.",
            true,
          );
          setTimeout(() => {
            handleClearStateAlert();
            cleanIndexDB();
            return navigate("/");
          }, 7000);
        }
        setTimeout(() => {
          setViewPhase(VIEW_PHASE.SKELETON);
        }, 6500);

        let disbursementData = await getInfoDisbursement(credit[0].credit_id);
        setDisbursementData(disbursementData);
        const disbursementState = await validateDisbursementValue(
          credit[0],
          disbursementData,
        );
        configData(
          credit[0],
          info,
          quotaStatus,
          deudahoy,
          disbursementData,
          disbursementState,
        );
        setCreditsInfo(credit);
        setCreditId(credit[0]?.credit_id);
        setQuotaInfo(quotaStatus);
      } catch (error) {
        console.log(error);
        return;
      }
    };

    validSessionAndTokenJwt()
      .then(async (isValidToken) => {
        if (isValidToken) {
          /* setTimeout(() => { */
          const loadData = async () => {
            if (localStorage.getItem("fromConfirmed") === "true") {
              await handleInitView();
              localStorage.setItem("fromConfirmed", "false");
              setDataReady(true);
            } else {
              await handleInitView();
              setDataReady(true);
            }
          };
          loadData();
          /* }, 6500); */
        } else {
          returnToSignin(navigate);
        }
      })
      .catch((e) => {
        console.log(e);
        returnToSignin(navigate);
      });
  }, [
    navigate,
    updateDatosVarios,
    setCreditId,
    setQuotaInfo,
    loadCredits,
    loadInfoUser,
    loadInfoAuth,
    loadInfoQuota,
    getLastActivity,
    getLastCredits,
    handleGetMyRewards,
    getInterest,
    handleSetRangeColor,
    parseLastCredits,
    handleUpdateQuotaInfo,
    cleanIndexDB,
    getInfoDisbursement,
    validateDisbursementValue,
    configData,
    handleOpenAlert,
    handleClearStateAlert,
  ]);

  useEffect(() => {
    registerServiceWorker()
      .then(async (registration) => {
        if (registration) {
          try {
            let permission = await requestPermissionNotification();
            if (permission) {
              await subscribeUser(registration, localStorage.getItem("doc"));
            }
          } catch (error) {
            console.log(error);
          }
        }
      })
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    if (viewPhase === VIEW_PHASE.SKELETON && dataReady) {
      setViewPhase(VIEW_PHASE.DATA);
    }
  }, [viewPhase, dataReady]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  if (viewPhase === VIEW_PHASE.ANIMATION) {
    return (
      <Fragment>
        <LoginSuccessAnimation
          onAnimationComplete={handleAnimationComplete}
          duration={ANIMATION_DURATIONS.LOGIN_SUCCESS}
        />
      </Fragment>
    );
  }

  if (viewPhase === VIEW_PHASE.SKELETON) {
    return (
      <Fragment>
        <DashboardMyCredit
          name={""}
          balance={""}
          current={"1"}
          title={"Mis créditos"}
          main={
            <>
              <div className="flex pb-6 gap-4 mt-10">
                <div className="w-full rounded-2xl shadow-2xl transition p-4 bg-purple text-white">
                  <div className="flex justify-between">
                    <h3 className="text-base">
                      <SkeletonDahsboard height={20} width={120} />
                    </h3>
                    <h3 className="text-base">
                      <SkeletonDahsboard height={10} width={30} />
                    </h3>
                  </div>
                  <div className="flex justify-between">
                    <div className="w-2/3 grid content-between">
                      <div>
                        <div className="block pt-14">
                          <div>
                            <div className=" text-tgray">
                              <SkeletonDahsboard height={20} width={120} />
                            </div>
                            <div>
                              <SkeletonDahsboard height={40} width={100} />
                            </div>
                          </div>
                          <div className="block pt-14">
                            <div className="text-tgray">
                              <SkeletonDahsboard height={20} width={130} />
                            </div>
                            <div>
                              <SkeletonDahsboard height={20} width={70} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Disbursement */}
                    <div className="block w-full py-8 px-0 sm:px-2 md:px-6">
                      <div className="text-tgray">
                        <SkeletonDahsboard height={20} width={140} />
                        <div className="flex gap-1">
                          <div className="text-white font-bold">
                            <SkeletonDahsboard height={20} width={110} />
                          </div>
                        </div>
                      </div>
                      <div className="pt-2">
                        <div className="block md:hidden">
                          <SkeletonDahsboard height={30} width={220} />
                        </div>
                        <div className="hidden md:block">
                          <SkeletonDahsboard height={50} width={300} />
                        </div>
                      </div>
                      <div className="pt-4 sm:pt-12">
                        <SkeletonDahsboard height={50} width={100} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="block sm:grid grid-cols-8 gap-4 mb-32 md:mb-0 px-6">
                <div className="block col-span-3 font-bold rounded-2xl transition p-4 mb-4 md:mb-0 bg-white">
                  <div className="flex">
                    <SkeletonDahsboard height={20} width={130} />
                  </div>
                  <div className="h-full pb-4 grid content-center justify-center">
                    <div className="grid content-end justify-center 2xl:w-60 w-48 h-28 border-t-8 border-x-8 rounded-t-full border-spacing-10 border-purpleLight">
                      <div className="font-bold text-4xl">
                        <SkeletonDahsboard height={60} width={100} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="block col-span-5 h-full rounded-2xl transition p-4 bg-white">
                  <h1 className="font-bold">
                    <SkeletonDahsboard height={20} width={160} />
                  </h1>
                  <div className="grid grid-cols-4 gap-2 pt-4 text-grayP">
                    <SkeletonDahsboard height={20} width={15} />
                    <SkeletonDahsboard height={20} width={30} />
                    <SkeletonDahsboard height={20} width={30} />
                    <SkeletonDahsboard height={20} width={40} />
                  </div>

                  {records.map((record) => (
                    <div
                      key={record.id}
                      className="grid grid-cols-4 gap-2 text-sm text-purple"
                    >
                      <SkeletonDahsboard height={20} width={60} />
                      <SkeletonDahsboard height={20} width={50} />
                      <SkeletonDahsboard height={20} width={50} />
                      <SkeletonDahsboard height={20} width={50} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          }
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      {viewalert && (
        <>
          <SuccessAlert
            isOpen={isOpenSuccess}
            message={messsageAlert}
          ></SuccessAlert>
          <ErrorAlert isOpen={isOpenError} message={messsageAlert}></ErrorAlert>
        </>
      )}
      <DashboardMyCredit
        name={capitalizeFirstLetter(
          basicInfo.firstname ? basicInfo.firstname : "",
        )}
        balance={
          creditsInfo[0]?.status === "Cancelado"
            ? 0
            : (datosVarios?.deudaHoy ?? 0)
        }
        current={"1"}
        title={"Mis créditos"}
        disclosures={disclosures}
        creditInfo={creditInfo}
        due={due}
        main={
          <>
            <div className="flex pb-6 gap-4 mt-10">
              {features &&
                features.length > 0 &&
                features.map((feature, index) =>
                  hasMora && feature.card === 1 ? (
                    <div
                      key={"div-feature-" + index}
                      className={feature.classBox}
                      id={feature?.idhtml}
                    >
                      <div
                        key={"div-feature-title-" + index}
                        className="flex justify-between pb-2"
                      >
                        <h3
                          key={"div-feature-title-h3-" + index}
                          className="text-base"
                        >
                          {feature.title}
                        </h3>

                        <h3
                          key={"div-feature-title2-h3-" + index}
                          className="text-base"
                        >
                          {feature.title2}
                        </h3>
                      </div>

                      {/* Div que contiene mensaje de credito en mora */}
                      <div
                        key={"div-feature-mora-efecto-" + index}
                        className="text-2xl card-1-mora-efecto flex justify-content-start"
                        style={{
                          borderRadius: "4px",
                          height: "2rem",
                          alignItems: "center",
                          width: "68%",
                        }}
                      >
                        <h2
                          key={"div-feature-mora-efecto-h2-" + index}
                          className="text-base pl-2 font-w500"
                        >{`Tú crédito presenta ${daysMora} días de mora.`}</h2>
                      </div>

                      <div className="" key={"div-feature-details-" + index}>
                        {feature.details.map((detail, i) => (
                          <div key={detail.id + i}>
                            <div className="pt-4 md:pt-4" key={"div-pt-4-" + i}>
                              <span
                                key={"span-nameO-" + i}
                                className=" text-grayM"
                              >
                                {detail.nameO}{" "}
                              </span>
                              <span key={detail.name} className=" text-grayP">
                                {detail.name}{" "}
                              </span>
                              <div
                                className="text-2xl font-bold"
                                key={"div-text-2xl-font-bold-" + i}
                              >
                                {detail.value}{" "}
                                <Numeral
                                  key={"div-numberO-numeral-" + i}
                                  className="text-2xl font-bold"
                                  format={"$0,0"}
                                  value={detail.numberO}
                                />
                                <Numeral
                                  key={"div-number-numeral-" + i}
                                  format={"$0,0"}
                                  value={detail.number}
                                />{" "}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div
                        className="flex justify-end"
                        key={"div-tertiary-button" + index}
                      >
                        <Tertiary text={feature.button} />
                      </div>
                    </div>
                  ) : (
                    // Tarjetas en tamaño monitor web
                    <Fragment key={"fragment-feature-" + index}>
                      <div
                        key={"feature-" + index}
                        className={feature.classBox}
                        id={feature?.idhtml}
                      >
                        <div
                          key={feature.title}
                          className="flex justify-between"
                        >
                          <h3 key={feature.title} className="text-base">
                            {feature.title}
                          </h3>

                          {optionsMenuEllipsisCard && (
                            <Fragment key={"fragment-options-menu-ellipsis"}>
                              <div className="hidden md:block">
                                <MenuEllipsis
                                  options={optionsMenuEllipsisCard}
                                />
                              </div>
                              <div className="z-50 block md:hidden">
                                <MenuEllipsisMobile
                                  options={optionsMenuEllipsisCard}
                                />
                              </div>
                            </Fragment>
                          )}
                        </div>
                        <div className="flex gap-4 justify-between">
                          <div className="w-2/3 grid content-between">
                            {feature.details.map((detail, idx) => (
                              <div className="" key={detail.id + idx}>
                                <div
                                  className="block pt-14"
                                  key={"block-pt-14-" + idx}
                                >
                                  <div className="" key={"div-block-" + idx}>
                                    <div
                                      key={"div-nameO-" + idx}
                                      className=" text-tgray"
                                    >
                                      {detail.nameO}{" "}
                                    </div>
                                    <div key={"div-numberO-" + idx}>
                                      <Numeral
                                        key={"div-numberO-numeral-" + idx}
                                        className="text-2xl font-bold"
                                        format={"$0,0"}
                                        value={detail.numberO}
                                      />
                                    </div>
                                    {detail.numberO > 0 ? (
                                      <div
                                        className="block"
                                        key={"div-block-2-" + idx}
                                      >
                                        <div
                                          className="text-tgray"
                                          key={"div-text-tgray-" + idx}
                                        >
                                          {detail.nameD}{" "}
                                        </div>
                                        <div key={"div-value-" + idx}>
                                          {formatFecha(detail.value)}
                                        </div>
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="block w-full pt-14 px-0 sm:px-2 md:px-6">
                            {feature.details.map((detail, i) => (
                              <div key={"div-disbursementName-" + i}>
                                <div key={"div-disbursementName-child" + i}>
                                  <div
                                    key={"div-disbursementName-child-child" + i}
                                    className="text-tgray"
                                  >
                                    {creditInfo?.status === "Vigente" &&
                                      creditInfo?.versionTarifas === "2" &&
                                      detail.disbursementName}
                                    <div
                                      key={"div-disbursementValue-" + i}
                                      className="flex gap-1"
                                    >
                                      <div
                                        key={"div-disbursementValue-" + i}
                                        className="text-white font-bold lg:text-xl"
                                      >
                                        {creditInfo?.status === "Vigente" &&
                                          creditInfo?.versionTarifas === "2" &&
                                          detail.disbursementValue}
                                        {/* {creditInfo?.status === "Vigente" && creditInfo?.versionTarifas === "2" && detail.disbursementValue} */}
                                      </div>

                                      <div
                                        key={"div-icon-" + i}
                                        className="w-4 text-white h-auto font-bold grid content-center"
                                      >
                                        {detail.icon}
                                      </div>
                                    </div>
                                  </div>
                                  <div key={"div-steps-" + i} className="pt-2">
                                    {detail.steps}{" "}
                                    <div
                                      key={"div-stepsName-" + i}
                                      className=""
                                    >
                                      <div
                                        key={"div-stepsName-div-" + i}
                                        className="flex justify-between w-full"
                                      >
                                        {}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {disbursementData?.status === "Fallido" && (
                              <a href="/mis-bancos" className="">
                                <SecondaryCard text="Actualizar cuenta bancaria" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  ),
                )}
            </div>
            {/* Tarjetas de Historial de créditos y Análisis financiero*/}
            <div className="block sm:grid grid-cols-8 gap-4 mb-32 md:mb-0 px-6">
              <div className="block col-span-3 font-bold rounded-2xl transition p-4 mb-4 md:mb-0 bg-white">
                <div className="flex">
                  <div>Puntaje modelo de decisión</div>
                  <QuestionMarkCircleIcon
                    data-tooltip-id="scr"
                    className="w-4 h-auto ml-0.5 cursor-pointer"
                  />
                  <Tooltip
                    place="top"
                    id="scr"
                    className="w-60"
                  >
                    Corresponde a la evaluación que hace Galilea con base en
                    la información suministrada en los formularios y la
                    información que hay en las bases de datos sobre ti
                  </Tooltip>
                </div>
                <div className="h-full pb-4 grid content-center justify-center">
                  {/* <div className="grid content-end justify-center 2xl:w-60 w-48 h-28 border-t-8 border-x-8 rounded-t-full border-spacing-10 border-purpleLight">
                    <div className="font-bold text-4xl">{scoreFromPolicy ?? 0}</div>
                  </div> */}
                  <RainbowRangeBasedUpperSemiCircularProgress
                    score={scoreEngine + 10}
                    size={200}
                    colorRanges={colorRanges}
                  />
                </div>
              </div>
              <div className="block col-span-5 h-full rounded-2xl transition p-4 bg-white">
                <h1 className="font-bold">
                  Histórico de créditos ({records.length})
                </h1>
                <div className="grid grid-cols-4 gap-2 pt-4 text-grayP">
                  <span>Id</span>
                  <span>Monto</span>
                  <span>Fecha</span>
                  <span>Estado</span>
                </div>

                {records.map((record, index) => (
                  <div
                    key={record.id}
                    className="grid grid-cols-4 gap-2 text-sm text-purple"
                  >
                    <span key={"span-id-" + index}>{record.id}</span>
                    <span key={"span-ammount-" + index}>{record.ammount}</span>
                    <span key={"span-date-" + index}>{record.date}</span>
                    <span key={"span-state-" + index}>{record.state}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        }
      />
    </Fragment>
  );
}
